import React, { useState, useEffect, useRef } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Brain, Heart, Users, ClipboardCheck, Star, Shield, Activity, Eye,
  Lightbulb, MessageCircle, Move, Hand, Scale, Microscope, Beaker,
  Leaf, Atom, Dna, Droplets, FileCheck, Calendar, Phone, Mail, MapPin,
  CheckCircle2, BookOpen, Target, Stethoscope, Sparkles, HeartHandshake,
  BarChart3, Lock, FileText, UserCheck, ShieldCheck, Menu, X, Quote,
  Zap, Puzzle, RefreshCw
} from "lucide-react";

/* ═══════════════════════════════════════════
   BRAND COLORS
   ═══════════════════════════════════════════ */
const B = {
  blue: "#18437E",
  blueDark: "#102d56",
  blueLight: "#2a5a9e",
  bluePale: "#e8f0fa",
  red: "#C92939",
  redPale: "#fdf0f1",
  orange: "#F88606",
  orangeLight: "#ffa033",
  orangePale: "#fff5e6",
  green: "#B0C94B",
  greenDark: "#8aa830",
  greenPale: "#f4f8e6",
  white: "#fff",
  offWhite: "#f8f9fc",
  gray100: "#eef1f5",
  gray200: "#dde2ea",
  gray400: "#8896a8",
  gray600: "#4a5568",
  gray800: "#1e2a3a",
  gray900: "#131c28",
  gradWarm: "linear-gradient(135deg, #F88606 0%, #C92939 100%)",
} as const;

/* ═══════════════════════════════════════════
   HOOKS & HELPERS
   ═══════════════════════════════════════════ */
function useInView(threshold: number = 0.1): [React.RefObject<HTMLDivElement | null>, boolean] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [vis, setVis] = useState<boolean>(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVis(true);
          obs.unobserve(el);
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, vis];
}

function sc(id: string): void {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
}

/* ═══════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════ */
interface FadeProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  y?: number;
}

const Fade: React.FC<FadeProps> = ({ children, className = "", delay = 0, y = 28 }) => {
  const [ref, vis] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: vis ? 1 : 0,
        transform: vis ? "none" : `translateY(${y}px)`,
        transition: `opacity 0.65s cubic-bezier(.22,1,.36,1) ${delay}s, transform 0.65s cubic-bezier(.22,1,.36,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

interface SLabelProps {
  children: React.ReactNode;
  color?: string;
}

const SLabel: React.FC<SLabelProps> = ({ children, color = B.orange }) => (
  <span
    style={{
      fontSize: 12, fontWeight: 800, letterSpacing: "0.15em",
      textTransform: "uppercase", color, display: "inline-block", marginBottom: 8,
    }}
  >
    {children}
  </span>
);

interface IBadgeProps {
  icon: LucideIcon;
  size?: number;
  bg?: string;
  color?: string;
}

const IBadge: React.FC<IBadgeProps> = ({ icon: Icon, size = 42, bg = B.bluePale, color = B.blue }) => (
  <div
    style={{
      width: size, height: size, borderRadius: 12, background: bg,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}
  >
    <Icon size={size * 0.5} color={color} strokeWidth={2} />
  </div>
);

/* ═══════════════════════════════════════════
   NAVBAR
   ═══════════════════════════════════════════ */
interface NavItem {
  l: string;
  id: string;
}

const NAV: NavItem[] = [
  { l: "About", id: "about" },
  { l: "Services", id: "services" },
  { l: "INAI Model", id: "inai" },
  /*{ l: "Nutrition", id: "nutrition" },*/
  { l: "Team", id: "team" },
  { l: "For Parents", id: "parents" },
  { l: "Contact", id: "contact" },
];

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState<boolean>(false);
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  return (
    <nav
      style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: scrolled ? "rgba(16,45,86,0.97)" : B.blueDark,
        backdropFilter: "blur(14px)",
        boxShadow: scrolled ? "0 2px 24px rgba(0,0,0,0.2)" : "none",
        transition: "all 0.3s",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
          {/* Logo */}
          <div
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 12px 4px 6px" }}
          >
            <img
              src="logoonly.png"
              alt="INGC"
              className="nav-logo-icon"
              style={{ height: 42, objectFit: "contain", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.3))" }}
            />
            <img
              src="logowithtext.png"
              alt="Impact Neurogrowth Centre"
              className="nav-logo-full"
              style={{ height: 44, objectFit: "contain", filter: "brightness(0) invert(1) brightness(1.1)" }}
            />
          </div>

          {/* Desktop Nav */}
          <div style={{ display: "flex", alignItems: "center", gap: 2 }} className="nav-desktop">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => sc(n.id)}
                style={{
                  background: "none", border: "none", color: "rgba(255,255,255,0.8)",
                  fontSize: 13, fontWeight: 500, padding: "8px 13px", borderRadius: 8,
                  cursor: "pointer", transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.background = "rgba(255,255,255,0.1)";
                  (e.target as HTMLButtonElement).style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.background = "none";
                  (e.target as HTMLButtonElement).style.color = "rgba(255,255,255,0.8)";
                }}
              >
                {n.l}
              </button>
            ))}
            <button
              onClick={() => sc("contact")}
              style={{
                background: B.orange, color: "#fff", border: "none", fontSize: 13,
                fontWeight: 700, padding: "9px 22px", borderRadius: 8, cursor: "pointer",
                marginLeft: 8, transition: "all 0.2s",
              }}
              onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.background = B.orangeLight; }}
              onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.background = B.orange; }}
            >
              Book Assessment
            </button>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="nav-mobile-btn"
            style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", display: "none" }}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="nav-mobile-menu" style={{ paddingBottom: 16, display: "none" }}>
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => { sc(n.id); setMobileOpen(false); }}
                style={{
                  display: "block", width: "100%", textAlign: "left", background: "none",
                  border: "none", color: "rgba(255,255,255,0.9)", fontSize: 15, fontWeight: 500,
                  padding: "12px 0", borderBottom: "1px solid rgba(255,255,255,0.08)", cursor: "pointer",
                }}
              >
                {n.l}
              </button>
            ))}
            <button
              onClick={() => { sc("contact"); setMobileOpen(false); }}
              style={{
                marginTop: 12, width: "100%", background: B.orange, color: "#fff",
                border: "none", fontSize: 14, fontWeight: 700, padding: "12px",
                borderRadius: 10, cursor: "pointer",
              }}
            >
              Book Assessment
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

/* ═══════════════════════════════════════════
   HERO
   ═══════════════════════════════════════════ */
const Hero: React.FC = () => (
  <section id="about" style={{ background: `linear-gradient(135deg, ${B.blueDark} 0%, ${B.blue} 50%, ${B.blueLight} 100%)`, paddingTop: 100, paddingBottom: 52, position: "relative", overflow: "hidden" }}>
    <div style={{ position: "absolute", top: -60, right: "10%", width: 250, height: 250, borderRadius: "50%", background: `radial-gradient(circle, ${B.orange}15, transparent 70%)` }} />
    <div style={{ position: "absolute", bottom: -40, left: "5%", width: 200, height: 200, borderRadius: "50%", background: `radial-gradient(circle, ${B.green}12, transparent 70%)` }} />
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 20px", position: "relative" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 36 }} className="grid-2col">
        <Fade>
          <div style={{ maxWidth: 560 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(248,134,6,0.2)", border: "1px solid rgba(248,134,6,0.3)", borderRadius: 24, padding: "6px 18px", marginBottom: 22 }}>
              <Sparkles size={14} color={B.orange} />
              <span style={{ color: B.orangeLight, fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>Integrated Pediatric Neurodevelopment Care</span>
            </div>
            <h1 style={{ color: "#fff", fontSize: "clamp(28px, 4.2vw, 46px)", fontWeight: 800, lineHeight: 1.12, letterSpacing: "-0.02em", margin: "0 0 20px" }}>
              Building Stronger{" "}
              <span style={{ background: B.gradWarm, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Foundations</span>
              {" "}for Lifelong Development
            </h1>
            <p style={{ color: "rgba(255,255,255,0.78)", fontSize: 16, lineHeight: 1.75, marginBottom: 30 }}>
              Every child's journey is unique. We provide structured, personalised, and coordinated therapy — rooted in science, delivered with care — to help your child reach their true potential.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              <button onClick={() => sc("contact")} style={{ background: B.orange, color: "#fff", border: "none", fontSize: 14, fontWeight: 700, padding: "13px 30px", borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "all 0.2s", boxShadow: `0 4px 18px ${B.orange}50` }} onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 6px 24px ${B.orange}70`; }} onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = `0 4px 18px ${B.orange}50`; }}>
                <ClipboardCheck size={16} /> Start with an Assessment
              </button>
              <button onClick={() => sc("inai")} style={{ background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)", fontSize: 14, fontWeight: 600, padding: "13px 30px", borderRadius: 10, cursor: "pointer", transition: "all 0.2s" }} onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.2)"; }} onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}>
                Learn Our Approach
              </button>
            </div>
          </div>
        </Fade>
        <Fade delay={0.15}>
          <div style={{ background: "rgba(255,255,255,0.07)", backdropFilter: "blur(16px)", borderRadius: 18, padding: 28, border: "1px solid rgba(255,255,255,0.12)" }}>
            <h3 style={{ color: "#fff", fontSize: 19, fontWeight: 700, marginBottom: 22 }}>Why Families Choose Us</h3>
            {([
              { icon: Puzzle, title: "Integrated, not isolated", desc: "therapies work together, not in silos", color: B.orange },
              { icon: BookOpen, title: "Evidence-informed", desc: "every intervention grounded in science", color: B.green },
              { icon: HeartHandshake, title: "Family partnership", desc: "parents are active participants", color: B.red },
              { icon: BarChart3, title: "Regular reviews", desc: "monthly structured progress updates", color: B.orange },
            ] as { icon: LucideIcon; title: string; desc: string; color: string }[]).map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "flex-start" }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: `${item.color}25`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <item.icon size={16} color={item.color} />
                </div>
                <div>
                  <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>{item.title}</span>
                  <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 13 }}> — {item.desc}</span>
                </div>
              </div>
            ))}
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.12)", marginTop: 20, paddingTop: 20, display: "flex", gap: 40 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ color: B.orange, fontSize: 30, fontWeight: 900 }}>5+</div>
                <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, fontWeight: 600 }}>Therapy Domains</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ color: B.green, fontSize: 24, fontWeight: 900, letterSpacing: "0.06em" }}>INAI</div>
                <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, fontWeight: 600 }}>Intensive Model</div>
              </div>
            </div>
          </div>
        </Fade>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════
   PILLARS
   ═══════════════════════════════════════════ */
const Pillars: React.FC = () => {
  const items: { icon: LucideIcon; title: string; desc: string; accent: string }[] = [
    { icon: Brain, title: "Neurodevelopmental Clarity", desc: "Understanding the why behind behaviour, not just managing it", accent: B.orange },
    { icon: Users, title: "Family-Centred Care", desc: "Parents involved at every stage of the therapy journey", accent: B.green },
    { icon: Calendar, title: "Structured Monthly Reviews", desc: "Clear communication on progress, goals, and next steps", accent: B.red },
    { icon: BookOpen, title: "Evidence-Informed Practice", desc: "Therapies backed by clinical research and best practice", accent: B.orange },
  ];
  return (
    <section style={{ background: B.gray900, padding: "44px 20px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
        {items.map((it, i) => (
          <Fade key={i} delay={i * 0.07}>
            <div
              style={{ background: "rgba(255,255,255,0.04)", borderRadius: 14, padding: 24, textAlign: "center", border: "1px solid rgba(255,255,255,0.06)", transition: "all 0.3s", position: "relative", overflow: "hidden" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.transform = "none"; }}
            >
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: it.accent }} />
              <div style={{ width: 48, height: 48, borderRadius: 12, background: `${it.accent}20`, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                <it.icon size={22} color={it.accent} />
              </div>
              <h4 style={{ color: "#fff", fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{it.title}</h4>
              <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 13, lineHeight: 1.5 }}>{it.desc}</p>
            </div>
          </Fade>
        ))}
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════
   PHILOSOPHY
   ═══════════════════════════════════════════ */
const Philosophy: React.FC = () => {
  const principles: { icon: LucideIcon; title: string; desc: string; color: string }[] = [
    { icon: Target, title: "Regulation Precedes Learning", desc: "A child cannot absorb, retain, or apply learning if their nervous system is dysregulated. We address this foundation first.", color: B.red },
    { icon: Brain, title: "Behaviour Reflects Neurology", desc: 'What looks like a behaviour challenge is often a communication of neurological need. We ask "why" before "what."', color: B.orange },
    { icon: Puzzle, title: "Integration Produces Better Outcomes", desc: "When therapies speak to each other, children show more connected and meaningful functional progress.", color: B.green },
  ];
  const focusAreas: { l: string; c: string }[] = [
    { l: "Early Assessment", c: B.blue }, { l: "Individualised Planning", c: B.orange },
    { l: "Family Collaboration", c: B.green }, { l: "Functional Outcomes", c: B.red },
    { l: "Regulation First", c: B.blue }, { l: "Ongoing Review", c: B.orange },
  ];
  return (
    <section style={{ background: B.offWhite, padding: "56px 20px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 36 }} className="grid-2col-lg">
          <Fade>
            <div>
              <SLabel color={B.red}>Our Philosophy</SLabel>
              <h2 style={{ color: B.blue, fontSize: "clamp(24px, 3vw, 34px)", fontWeight: 800, lineHeight: 1.2, marginBottom: 16 }}>We Look Beyond Symptoms to Understand Your Child</h2>
              <p style={{ color: B.gray600, fontSize: 15, lineHeight: 1.75, marginBottom: 28 }}>At Impact Neurogrowth Centre, we believe behaviour reflects underlying neurological processes — not defiance, not failure. Our approach begins with understanding, then builds systematically.</p>
              {principles.map((p, i) => (
                <Fade key={i} delay={i * 0.1}>
                  <div style={{ display: "flex", gap: 14, marginBottom: 16, padding: 18, background: "#fff", borderRadius: 14, borderLeft: `4px solid ${p.color}`, boxShadow: "0 2px 12px rgba(0,0,0,0.04)", transition: "all 0.3s" }} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "translateX(4px)"; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.04)"; e.currentTarget.style.transform = "none"; }}>
                    <IBadge icon={p.icon} bg={`${p.color}15`} color={p.color} />
                    <div>
                      <h4 style={{ color: B.blue, fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{p.title}</h4>
                      <p style={{ color: B.gray600, fontSize: 13, lineHeight: 1.6 }}>{p.desc}</p>
                    </div>
                  </div>
                </Fade>
              ))}
            </div>
          </Fade>
          <Fade delay={0.12}>
            <div style={{ background: "#fff", borderRadius: 18, padding: 28, boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
              <h3 style={{ color: B.blue, fontSize: 20, fontWeight: 700, marginBottom: 20 }}>Our Clinical Focus Areas</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {focusAreas.map((a, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", background: `${a.c}08`, borderRadius: 10, border: `1px solid ${a.c}18`, transition: "all 0.25s", cursor: "default" }}>
                    <CheckCircle2 size={16} color={a.c} />
                    <span style={{ color: a.c, fontSize: 13, fontWeight: 600 }}>{a.l}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 24, padding: 20, background: B.orangePale, borderRadius: 12, borderLeft: `3px solid ${B.orange}` }}>
                <p style={{ color: B.gray600, fontSize: 13, lineHeight: 1.7, fontStyle: "italic" }}>We understand the pressure Indian families feel — balancing school expectations, extended family opinions, and limited access to clear guidance. Our team is here to be a steady, informed partner alongside you.</p>
              </div>
            </div>
          </Fade>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════
   INAI MODEL
   ═══════════════════════════════════════════ */
const InaiModel: React.FC = () => {
  const domains: { icon: LucideIcon; title: string; desc: string; color: string }[] = [
    { icon: Brain, title: "Neuro Regulation", desc: "Building the neurological foundation for calm, focused engagement", color: B.blue },
    { icon: Hand, title: "Motor–Sensory Integration", desc: "Supporting how the body receives and responds to sensory information", color: B.orange },
    { icon: Lightbulb, title: "Cognitive & Executive Development", desc: "Attention, planning, working memory, and learning readiness", color: B.green },
    { icon: MessageCircle, title: "Communication & Functional Expression", desc: "Language, articulation, and the ability to express wants and needs", color: B.red },
    { icon: Heart, title: "Emotional & Behavioural Regulation", desc: "Managing emotional responses and building adaptive behaviours", color: B.orange },
  ];
  const features: { icon: LucideIcon; title: string; desc: string; color: string }[] = [
    { icon: ClipboardCheck, title: "Assessment-Led", desc: "All plans begin with thorough assessment of your child's strengths and needs", color: B.blue },
    { icon: RefreshCw, title: "Ongoing Review", desc: "Monthly clinical reviews adapt the plan as your child grows", color: B.green },
    { icon: Activity, title: "Regulation-Sensitive", desc: "Sessions adjusted based on your child's daily regulation levels", color: B.orange },
    { icon: Target, title: "Functionally Focused", desc: "Goals tied to real-world outcomes: school, home, community", color: B.red },
  ];
  return (
    <section id="inai" style={{ background: "#fff", padding: "56px 20px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Fade>
          <SLabel color={B.green}>Our Core Model</SLabel>
          <h2 style={{ color: B.blue, fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 800, lineHeight: 1.2, marginBottom: 12 }}>Impact NeuroAdaptive Intensive <span style={{ color: B.red }}>(INAI)</span></h2>
          <p style={{ color: B.gray600, fontSize: 15, lineHeight: 1.75, maxWidth: 700, marginBottom: 36 }}>A structured, integrative therapy framework for children who need coordinated support across multiple developmental areas. The INAI model coordinates interventions within structured session blocks — adjusted continuously based on assessment findings and your child's evolving needs.</p>
        </Fade>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 28 }} className="grid-2col-lg">
          <Fade>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {features.map((f, i) => (
                <div key={i} style={{ padding: 22, borderRadius: 14, border: `2px solid ${f.color}15`, background: `${f.color}04`, transition: "all 0.3s" }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = f.color; e.currentTarget.style.boxShadow = `0 4px 16px ${f.color}15`; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${f.color}15`; e.currentTarget.style.boxShadow = "none"; }}>
                  <IBadge icon={f.icon} size={38} bg={`${f.color}15`} color={f.color} />
                  <h4 style={{ color: B.blue, fontSize: 14, fontWeight: 700, margin: "12px 0 4px" }}>{f.title}</h4>
                  <p style={{ color: B.gray600, fontSize: 12, lineHeight: 1.6 }}>{f.desc}</p>
                </div>
              ))}
            </div>
          </Fade>
          <Fade delay={0.1}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {domains.map((d, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px 20px", background: `${d.color}08`, borderRadius: 14, border: `1px solid ${d.color}12`, transition: "all 0.3s", cursor: "default" }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: d.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#fff", fontSize: 14, fontWeight: 800 }}>{String(i + 1).padStart(2, "0")}</div>
                  <div>
                    <h4 style={{ color: B.blue, fontSize: 14, fontWeight: 700 }}>{d.title}</h4>
                    <p style={{ color: B.gray600, fontSize: 12, lineHeight: 1.5 }}>{d.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Fade>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════
   SERVICES
   ═══════════════════════════════════════════ */
const Services: React.FC = () => {
  const svcs: { icon: LucideIcon; title: string; desc: string; tag: string; color: string }[] = [
    { icon: Activity, title: "Neurofeedback Therapy", desc: "A non-invasive, EEG-based approach that supports self-regulation through monitored brainwave activity. Sessions are calm and child-friendly.", tag: "Non-Invasive", color: B.blue },
    { icon: Zap, title: "Photobiomodulation (PBM)", desc: "A gentle, light-based therapy designed to support neurodevelopmental processes. Child-friendly, non-invasive format.", tag: "Light Therapy", color: B.orange },
    { icon: Eye, title: "Sensory Integration", desc: "Structured intervention helping children process and respond to sensory input — reducing overwhelm, improving participation.", tag: "Sensory Processing", color: B.green },
    { icon: Move, title: "Rhythmic Movement Training", desc: "Movement-based intervention supporting neurological organisation, coordination, and regulation through rhythmic activity.", tag: "Movement & Rhythm", color: B.red },
    { icon: Hand, title: "Physical Therapy", desc: "Targeted support for posture, strength, movement quality, balance, and functional mobility with intensive options.", tag: "Motor Development", color: B.blue },
    { icon: Scale, title: "Hemispheric Balance", desc: "Activities to support bilateral integration, coordination, and learning readiness — foundational for academic tasks.", tag: "Brain Integration", color: B.orange },
    { icon: MessageCircle, title: "Speech Therapy", desc: "Support for receptive/expressive language, articulation, and functional communication. Intensive options also available.", tag: "Communication", color: B.green },
  ];
  return (
    <section id="services" style={{ background: `linear-gradient(180deg, ${B.bluePale} 0%, ${B.offWhite} 100%)`, padding: "56px 20px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Fade>
          <SLabel color={B.orange}>What We Offer</SLabel>
          <h2 style={{ color: B.blue, fontSize: "clamp(24px, 3vw, 34px)", fontWeight: 800, marginBottom: 8 }}>Our Therapy Services</h2>
          <p style={{ color: B.gray600, fontSize: 15, lineHeight: 1.75, maxWidth: 620, marginBottom: 32 }}>Each service can be delivered independently or within the INAI intensive framework — the right combination is determined through assessment.</p>
        </Fade>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
          {svcs.map((s, i) => (
            <Fade key={i} delay={i * 0.05}>
              <div style={{ background: "#fff", borderRadius: 16, padding: 24, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.04)", transition: "all 0.3s", display: "flex", flexDirection: "column", borderTop: `4px solid ${s.color}` }} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 8px 28px ${s.color}18`; e.currentTarget.style.transform = "translateY(-4px)"; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.04)"; e.currentTarget.style.transform = "none"; }}>
                <IBadge icon={s.icon} size={44} bg={`${s.color}12`} color={s.color} />
                <h4 style={{ color: B.blue, fontSize: 16, fontWeight: 700, margin: "14px 0 8px" }}>{s.title}</h4>
                <p style={{ color: B.gray600, fontSize: 13, lineHeight: 1.65, flex: 1 }}>{s.desc}</p>
                <span style={{ display: "inline-block", marginTop: 14, padding: "5px 14px", background: `${s.color}10`, borderRadius: 6, fontSize: 11, fontWeight: 700, color: s.color }}>{s.tag}</span>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════
   NUTRITION
   ═══════════════════════════════════════════ 
const Nutrition: React.FC = () => {
  const factors: { icon: LucideIcon; l: string }[] = [
    { icon: Brain, l: "Brain Regulation & Focus" }, { icon: Beaker, l: "Nutritional Sufficiency" },
    { icon: Leaf, l: "Gut Health & Microbiome" }, { icon: Droplets, l: "Blood Sugar Stability" },
    { icon: Atom, l: "Metabolic Function" }, { icon: Microscope, l: "Toxin & Sensitivity Load" },
  ];
  const tests: { icon: LucideIcon; title: string; desc: string; color: string }[] = [
    { icon: Stethoscope, title: "Basic Blood Work", desc: "Comprehensive baseline — nutritional markers, inflammatory indicators, metabolic function.", color: B.blue },
    { icon: Leaf, title: "Food Sensitivity Testing", desc: "Identifies IgG-mediated food reactions creating hidden inflammation affecting brain function.", color: B.green },
    { icon: Beaker, title: "Organic Acids Test", desc: "Assesses metabolic function, gut health, nutritional deficiencies, cellular energy production.", color: B.orange },
    { icon: Atom, title: "Environmental Toxins", desc: "Screens for toxins that accumulate and interfere with neurological development.", color: B.red },
    { icon: Microscope, title: "Heavy Metal Toxicity", desc: "Tests for lead, mercury, arsenic — which can significantly impact brain development.", color: B.blue },
    { icon: Dna, title: "DNA Methylation Profile", desc: "Examines detoxification, neurotransmitter production, and cellular repair pathways.", color: B.green },
  ];
  const gutPoints: string[] = [
    "Gut health directly influences brain regulation and emotional behaviour",
    "Food sensitivities may mimic or worsen ADHD, anxiety, and sensory challenges",
    "Blood sugar instability can cause attention and mood fluctuations",
    "Nutritional deficiencies are addressable — impact on development is significant",
    "Environmental toxin exposure is more common than families realise",
  ];
  return (
    <section id="nutrition" style={{ background: "#fff", padding: "56px 20px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Fade>
          <SLabel color={B.green}>Functional Medicine & Nutrition</SLabel>
          <h2 style={{ color: B.blue, fontSize: "clamp(24px, 3vw, 34px)", fontWeight: 800, marginBottom: 8 }}>Optimal Brain Function Requires Proper Nutrition</h2>
          <p style={{ color: B.gray600, fontSize: 15, lineHeight: 1.75, maxWidth: 700, marginBottom: 32 }}>Therapy alone is not enough. Nutrition is a foundational pillar — a brain that lacks the right building blocks cannot regulate, learn, or grow as well as it should.</p>
        </Fade>
        <Fade>
          <div style={{ background: `linear-gradient(135deg, ${B.greenDark} 0%, #3a6030 50%, ${B.blue} 100%)`, borderRadius: 18, padding: 32, marginBottom: 36 }}>
            <h3 style={{ color: "#fff", fontSize: 19, fontWeight: 700, marginBottom: 6 }}>The Nutrition–Brain Cycle</h3>
            <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 13, marginBottom: 20 }}>Every factor can be assessed, identified, and addressed as part of your child's integrated care plan.</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
              {factors.map((f, i) => (
                <div key={i} style={{ background: "rgba(255,255,255,0.1)", borderRadius: 10, padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, border: "1px solid rgba(255,255,255,0.08)" }}>
                  <f.icon size={18} color="rgba(255,255,255,0.85)" />
                  <span style={{ color: "#fff", fontSize: 12, fontWeight: 600 }}>{f.l}</span>
                </div>
              ))}
            </div>
          </div>
        </Fade>
        <Fade><SLabel color={B.red}>Functional Testing</SLabel><h3 style={{ color: B.blue, fontSize: 22, fontWeight: 800, marginBottom: 24 }}>Finding the Root Cause</h3></Fade>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
          {tests.map((t, i) => (
            <Fade key={i} delay={i * 0.05}>
              <div style={{ background: `${t.color}06`, borderRadius: 14, padding: 22, transition: "all 0.3s", border: `1px solid ${t.color}12` }} onMouseEnter={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = t.color; e.currentTarget.style.boxShadow = `0 4px 16px ${t.color}15`; }} onMouseLeave={(e) => { e.currentTarget.style.background = `${t.color}06`; e.currentTarget.style.borderColor = `${t.color}12`; e.currentTarget.style.boxShadow = "none"; }}>
                <IBadge icon={t.icon} size={38} bg={`${t.color}15`} color={t.color} />
                <h4 style={{ color: B.blue, fontSize: 14, fontWeight: 700, margin: "12px 0 6px" }}>{t.title}</h4>
                <p style={{ color: B.gray600, fontSize: 12, lineHeight: 1.6 }}>{t.desc}</p>
              </div>
            </Fade>
          ))}
        </div>
        <Fade>
          <div style={{ marginTop: 32, background: B.greenPale, borderRadius: 18, padding: 28, display: "grid", gridTemplateColumns: "1fr", gap: 24, border: `1px solid ${B.green}20` }} className="grid-2col-lg">
            <div>
              <h3 style={{ color: B.greenDark, fontSize: 22, fontWeight: 800, marginBottom: 12 }}>The Gut is the Second Brain</h3>
              <p style={{ color: B.gray600, fontSize: 14, lineHeight: 1.75 }}>Many children with developmental challenges show signs of gut dysfunction that — when addressed — unlock meaningful progress in therapy. Our functional medicine approach considers the full picture.</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {gutPoints.map((t, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <CheckCircle2 size={16} color={B.green} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span style={{ color: B.gray800, fontSize: 13, lineHeight: 1.55 }}>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </Fade>
      </div>
    </section>
  );
};*/

/* ═══════════════════════════════════════════
   INTENSIVE PROGRAMS (hidden for now — uncomment when needed)
   ═══════════════════════════════════════════ */
// const IntensivePrograms: React.FC = () => {
//   const steps: { num: string; title: string; desc: string; color: string }[] = [
//     { num: "01", title: "Comprehensive Assessment", desc: "We evaluate strengths, challenges, and priorities across all developmental domains before building any plan.", color: B.orange },
//     { num: "02", title: "Coordinated Therapy Blocks", desc: "Structured sessions where therapies are sequenced and coordinated — your child's team communicates continuously.", color: B.green },
//     { num: "03", title: "Ongoing Adjustment", desc: "The plan adapts with your child. Monthly reviews ensure therapy stays relevant as goals are met.", color: B.red },
//   ];
//   return (
//     <section style={{ background: `linear-gradient(135deg, ${B.blueDark} 0%, ${B.blue} 60%, #2a5070 100%)`, padding: "56px 20px", position: "relative", overflow: "hidden" }}>
//       <div style={{ position: "absolute", top: 0, right: 0, width: 300, height: 300, background: `radial-gradient(circle, ${B.orange}10, transparent 70%)` }} />
//       <div style={{ maxWidth: 1200, margin: "0 auto", textAlign: "center", position: "relative" }}>
//         <Fade>
//           <SLabel color={B.orangeLight}>Intensive Programs</SLabel>
//           <h2 style={{ color: "#fff", fontSize: "clamp(24px, 3.5vw, 36px)", fontWeight: 800, marginBottom: 8 }}>For Children Who Need More Coordinated Support</h2>
//           <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 15, lineHeight: 1.75, maxWidth: 640, margin: "0 auto 36px" }}>When multiple areas of development need simultaneous attention, our structured intensive formats provide the coordination and depth your child needs.</p>
//         </Fade>
//         <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
//           {steps.map((s, i) => (
//             <Fade key={i} delay={i * 0.1}>
//               <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 18, padding: 30, textAlign: "left", border: "1px solid rgba(255,255,255,0.08)", borderTop: `3px solid ${s.color}` }}>
//                 <div style={{ color: s.color, fontSize: 38, fontWeight: 900, lineHeight: 1 }}>{s.num}</div>
//                 <h4 style={{ color: "#fff", fontSize: 17, fontWeight: 700, margin: "14px 0 8px" }}>{s.title}</h4>
//                 <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 13, lineHeight: 1.7 }}>{s.desc}</p>
//               </div>
//             </Fade>
//           ))}
//         </div>
//       </div>
//     </section>
//   );
// };

/* ═══════════════════════════════════════════
   THERAPY JOURNEY
   ═══════════════════════════════════════════ */
const TherapyJourney: React.FC = () => {
  const items: { icon: LucideIcon; title: string; desc: string; color: string }[] = [
    { icon: BarChart3, title: "Progress is Gradual", desc: "Neurodevelopmental change takes time. We celebrate small wins as indicators of deeper, lasting progress.", color: B.blue },
    { icon: Activity, title: "Temporary Fluctuations are Normal", desc: "During regulation phases, some children show brief behavioural changes. This is recognised as part of the process.", color: B.orange },
    { icon: Calendar, title: "Monthly Structured Reviews", desc: "Regular, structured updates on progress, goal status, and upcoming plan adjustments.", color: B.green },
    { icon: RefreshCw, title: "Plans Evolve with Your Child", desc: "Therapy plans are living documents. As needs shift, the plan adjusts — never static, always relevant.", color: B.red },
  ];
  return (
    <section style={{ background: B.offWhite, padding: "56px 20px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 32 }} className="grid-2col-lg">
          <Fade>
            <div>
              <SLabel color={B.blue}>What to Expect</SLabel>
              <h2 style={{ color: B.blue, fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 800, marginBottom: 16 }}>Understanding the Therapy Journey</h2>
              <p style={{ color: B.gray600, fontSize: 14, lineHeight: 1.75, marginBottom: 24 }}>We believe informed families make better partners. Here is what to expect when your child begins therapy with us.</p>
              {items.map((item, i) => (
                <Fade key={i} delay={i * 0.07}>
                  <div style={{ display: "flex", gap: 14, marginBottom: 14, padding: 16, background: "#fff", borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.04)", borderLeft: `3px solid ${item.color}` }}>
                    <IBadge icon={item.icon} size={38} bg={`${item.color}12`} color={item.color} />
                    <div>
                      <h4 style={{ color: B.blue, fontSize: 14, fontWeight: 700, marginBottom: 3 }}>{item.title}</h4>
                      <p style={{ color: B.gray600, fontSize: 12, lineHeight: 1.6 }}>{item.desc}</p>
                    </div>
                  </div>
                </Fade>
              ))}
            </div>
          </Fade>
          <Fade delay={0.12}>
            <div style={{ background: B.gradWarm, borderRadius: 18, padding: 34, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Quote size={36} color="rgba(255,255,255,0.25)" style={{ marginBottom: 18 }} />
              <p style={{ color: "#fff", fontSize: 21, fontWeight: 600, lineHeight: 1.5, fontStyle: "italic", marginBottom: 14 }}>"Every child deserves to be understood — not just managed."</p>
              <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 14, marginBottom: 30 }}>When we truly understand how a child's brain works, we unlock the path to real, lasting progress.</p>
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: 24 }}>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)" }}>Our Commitment to Every Family</span>
                <p style={{ color: "rgba(255,255,255,0.9)", fontSize: 14, lineHeight: 1.75, marginTop: 10 }}>We understand the day-to-day challenges you navigate — managing routines, supporting your child's needs, and wondering if you're doing the right things. Our team is here to give you a clear and honest picture of your child's journey</p>
              </div>
            </div>
          </Fade>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════
   TEAM
   ═══════════════════════════════════════════ */
const Team: React.FC = () => {
  const members: { i: string; r: string; d: string; c: string }[] = [
    { i: "CD", r: "Centre Director · Clinical Lead", d: "Oversees clinical direction, therapy planning, family communication, and programme quality across all services.", c: B.blue },
    { i: "NF", r: "Neurofeedback Therapist", d: "Supports brain self-regulation through structured, non-invasive neurofeedback sessions.", c: B.orange },
    { i: "OT", r: "OT · Sensory Integration Therapist", d: "Supports sensory processing, motor planning, functional participation, and self-regulation.", c: B.green },
    { i: "PT", r: "Physical Therapist", d: "Focuses on posture, gross motor development, strength, coordination, and movement efficiency.", c: B.red },
    { i: "ST", r: "Speech & Communication Therapist", d: "Supports language development, articulation, communication skills, and functional expression.", c: B.orange },
    { i: "PC", r: "Parent Coordination · Programme Support", d: "Your primary point of contact for scheduling, reviews, coordination, and communication.", c: B.green },
  ];
  return (
    <section id="team" style={{ background: "#fff", padding: "48px 20px" }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <Fade>
          <SLabel color={B.red}>Meet the Team</SLabel>
          <h2 style={{ color: B.blue, fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 800, marginBottom: 8 }}>The People Supporting Your Child</h2>
          <p style={{ color: B.gray600, fontSize: 14, lineHeight: 1.75, maxWidth: 620, marginBottom: 32 }}>Our team works collaboratively to provide structured, child-centred, integrative neurodevelopmental support.</p>
        </Fade>
        <div className="team-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14 }}>
          {members.map((m, idx) => (
            <Fade key={idx} delay={idx * 0.06}>
              <div style={{ background: B.offWhite, borderRadius: 14, padding: 20, textAlign: "center", transition: "all 0.3s", border: "1px solid transparent", borderTop: `3px solid ${m.c}` }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${m.c}30`; e.currentTarget.style.background = "#fff"; e.currentTarget.style.boxShadow = `0 6px 20px ${m.c}12`; e.currentTarget.style.transform = "translateY(-3px)"; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = "transparent"; e.currentTarget.style.background = B.offWhite; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}>
                <div style={{ width: 58, height: 58, borderRadius: "50%", background: m.c, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 19, marginBottom: 14, boxShadow: `0 4px 12px ${m.c}30` }}>{m.i}</div>
                <p style={{ color: m.c, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10, lineHeight: 1.4 }}>{m.r}</p>
                <p style={{ color: B.gray600, fontSize: 12, lineHeight: 1.6 }}>{m.d}</p>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════
   TESTIMONIALS
   ═══════════════════════════════════════════ */
const Testimonials: React.FC = () => {
  const reviews: { tags: string; color: string; text: string; name: string; detail: string }[] = [
    { tags: "ASD · Communication · Functional Testing", color: B.blue, text: "Our son Ishaan has mid-range ASD and has been in therapy here for about six months — the progress has been remarkable. His ability to communicate verbally has improved significantly. Blood tests, hair tests, and gut tests identified core issues that other therapies had not addressed.", name: "Mukeshkumar & Subathra D.", detail: "Parents of Ishaan, Age 9 · Chennai" },
    { tags: "Speech · Behaviour · Neurofeedback", color: B.orange, text: "Before starting therapy here, my son would only say a few memorised words. Now — after three months — he is talking based on the situation. In the very first month I saw a difference in behaviour. He asks for his needs and responds to others.", name: "Anu Priyadharshini", detail: "Mother of 6-year-old · Chennai" },
    { tags: "Assessment · Sensory · Primitive Reflexes", color: B.green, text: "The assessment was a game-changer for our 7-year-old. After years of managing challenges, we finally got real answers. They explained everything in clear, simple terms. We left with a personalised plan and genuine hope.", name: "MS", detail: "Parent of 7-year-old · Chennai" },
  ];
  return (
    <section style={{ background: B.offWhite, padding: "56px 20px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Fade>
          <SLabel color={B.orange}>Parent Voices</SLabel>
          <h2 style={{ color: B.blue, fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 800, marginBottom: 6 }}>What Families Say</h2>
          <p style={{ color: B.gray600, fontSize: 14, marginBottom: 32 }}>Real experiences from parents who have walked this journey with us.</p>
        </Fade>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 18 }}>
          {reviews.map((r, i) => (
            <Fade key={i} delay={i * 0.1}>
              <div style={{ background: "#fff", borderRadius: 18, overflow: "hidden", boxShadow: "0 2px 14px rgba(0,0,0,0.05)", display: "flex", flexDirection: "column", height: "100%", transition: "all 0.3s", borderTop: `4px solid ${r.color}` }} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 8px 28px ${r.color}15`; e.currentTarget.style.transform = "translateY(-4px)"; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 14px rgba(0,0,0,0.05)"; e.currentTarget.style.transform = "none"; }}>
                <div style={{ padding: "16px 22px", background: `${r.color}06` }}>
                  <div style={{ display: "flex", gap: 3, marginBottom: 8 }}>{Array(5).fill(0).map((_, j) => <Star key={j} size={14} color={B.orange} fill={B.orange} />)}</div>
                  <span style={{ color: r.color, fontSize: 11, fontWeight: 700 }}>{r.tags}</span>
                </div>
                <div style={{ padding: "18px 22px", flex: 1 }}><p style={{ color: B.gray600, fontSize: 13, lineHeight: 1.7 }}>{r.text}</p></div>
                <div style={{ padding: "14px 22px", borderTop: `1px solid ${B.gray200}`, display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: r.color, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 14 }}>{r.name.charAt(0)}</div>
                  <div>
                    <div style={{ color: B.gray800, fontSize: 13, fontWeight: 700 }}>{r.name}</div>
                    <div style={{ color: B.gray400, fontSize: 11 }}>{r.detail}</div>
                  </div>
                </div>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════
   FOR PARENTS
   ═══════════════════════════════════════════ */
const ForParents: React.FC = () => {
  const items: { icon: LucideIcon; title: string; desc: string; color: string }[] = [
    { icon: MessageCircle, title: "Structured Communication Channels", desc: "Updates, questions, and concerns handled through clear, designated channels.", color: B.blue },
    { icon: BarChart3, title: "Monthly Progress Reviews", desc: "Every month, a structured update on goals achieved, areas developing, and the plan ahead.", color: B.green },
    { icon: FileText, title: "Clear Therapy Planning", desc: "You will always know what is being worked on and why. Plans are shared and adjusted with you.", color: B.orange },
    { icon: Shield, title: "Professional Boundaries That Protect Care", desc: "Our boundaries exist to maintain quality. Concerns are always welcomed through management.", color: B.red },
  ];
  const quotes: { text: string; label: string; color: string }[] = [
    { text: "I just want someone to explain what is actually happening with my child — not generic advice.", label: "Clarity over generality", color: B.orange },
    { text: "There is so much pressure from family. I need to be able to show progress.", label: "Documented, shareable progress", color: B.green },
    { text: "I don't want to feel like I'm being managed. I want to feel like a partner.", label: "Genuine collaboration", color: B.red },
  ];
  return (
    <section id="parents" style={{ background: "#fff", padding: "56px 20px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 32 }} className="grid-2col-lg">
          <Fade>
            <div>
              <SLabel color={B.green}>For Parents</SLabel>
              <h2 style={{ color: B.blue, fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 800, marginBottom: 8 }}>You Are Part of the Team</h2>
              <p style={{ color: B.gray600, fontSize: 14, lineHeight: 1.75, marginBottom: 24 }}>When parents find a centre they truly trust, they commit wholeheartedly. We honour that trust by staying structured, transparent, and consistently responsive.</p>
              {items.map((it, i) => (
                <Fade key={i} delay={i * 0.07}>
                  <div style={{ display: "flex", gap: 14, marginBottom: 14, padding: 16, background: `${it.color}06`, borderRadius: 12, border: `1px solid ${it.color}10` }}>
                    <IBadge icon={it.icon} size={38} bg={`${it.color}15`} color={it.color} />
                    <div>
                      <h4 style={{ color: B.blue, fontSize: 14, fontWeight: 700, marginBottom: 3 }}>{it.title}</h4>
                      <p style={{ color: B.gray600, fontSize: 12, lineHeight: 1.6 }}>{it.desc}</p>
                    </div>
                  </div>
                </Fade>
              ))}
            </div>
          </Fade>
          <Fade delay={0.12}>
            <div style={{ background: `linear-gradient(135deg, ${B.blue} 0%, ${B.blueDark} 100%)`, borderRadius: 18, padding: 34 }}>
              <h3 style={{ color: "#fff", fontSize: 20, fontWeight: 700, marginBottom: 26 }}>What Parents Tell Us They Need Most</h3>
              {quotes.map((q, i) => (
                <div key={i} style={{ marginBottom: 22, paddingBottom: 22, borderBottom: i < quotes.length - 1 ? "1px solid rgba(255,255,255,0.1)" : "none" }}>
                  <p style={{ color: "rgba(255,255,255,0.9)", fontSize: 14, fontStyle: "italic", lineHeight: 1.65, marginBottom: 8 }}>"{q.text}"</p>
                  <span style={{ color: q.color, fontSize: 12, fontWeight: 700 }}>{q.label}</span>
                </div>
              ))}
            </div>
          </Fade>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════
   SAFETY / ETHICS / PRIVACY
   ═══════════════════════════════════════════ */
const SafetyEthics: React.FC = () => {
  const items: { icon: LucideIcon; title: string; desc: string; color: string }[] = [
    { icon: ShieldCheck, title: "Non-Invasive Therapies", desc: "No surgery, no injections, no electrical stimulation. We clearly explain every modality.", color: B.blue },
    { icon: FileCheck, title: "Informed Parental Consent", desc: "We never proceed without your full consent. You can ask questions and say no.", color: B.orange },
    { icon: UserCheck, title: "Individualised at All Times", desc: "No two children receive the same plan. Every programme is built specifically for your child.", color: B.green },
    { icon: Lock, title: "Data Privacy", desc: "Health data used only for therapy planning, documentation, and compliance — never shared without consent.", color: B.red },
    { icon: FileText, title: "Your Data Rights", desc: "You may request access, correction, or erasure of records under relevant law.", color: B.blue },
    { icon: Shield, title: "Right to Withdraw Consent", desc: "You may withdraw consent in writing at any time. We explain implications first.", color: B.orange },
  ];
  return (
    <section style={{ background: B.offWhite, padding: "56px 20px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Fade>
          <SLabel color={B.red}>Safety · Ethics · Privacy</SLabel>
          <h2 style={{ color: B.blue, fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 800, marginBottom: 8 }}>Your Trust is Our Foundation</h2>
          <p style={{ color: B.gray600, fontSize: 14, lineHeight: 1.75, maxWidth: 620, marginBottom: 32 }}>Every decision is guided by your child's safety, your family's rights, and the highest clinical ethics.</p>
        </Fade>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
          {items.map((it, i) => (
            <Fade key={i} delay={i * 0.05}>
              <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 2px 10px rgba(0,0,0,0.04)", borderTop: `4px solid ${it.color}`, transition: "all 0.3s" }} onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = `0 6px 22px ${it.color}12`; }} onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.04)"; }}>
                <IBadge icon={it.icon} size={42} bg={`${it.color}12`} color={it.color} />
                <h4 style={{ color: B.blue, fontSize: 14, fontWeight: 700, margin: "14px 0 6px" }}>{it.title}</h4>
                <p style={{ color: B.gray600, fontSize: 12, lineHeight: 1.6 }}>{it.desc}</p>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════
   CTA + FOOTER
   ═══════════════════════════════════════════ */
const CtaFooter: React.FC = () => {
  return (
    <>
      <section id="contact" style={{ background: `linear-gradient(135deg, ${B.blue} 0%, ${B.blueDark} 40%, #1a1a2e 100%)`, padding: "68px 20px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -80, left: "20%", width: 260, height: 260, background: `radial-gradient(circle, ${B.orange}12, transparent 70%)` }} />
        <div style={{ maxWidth: 640, margin: "0 auto", position: "relative" }}>
          <Fade>
            <img src="logoonly.png" alt="INGC" style={{ height: 80, objectFit: "contain", margin: "0 auto 20px", display: "block", filter: "drop-shadow(0 2px 10px rgba(0,0,0,0.4))" }} />
            <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", color: B.orange }}>Begin the Journey</span>
            <h2 style={{ color: "#fff", fontSize: "clamp(26px, 4vw, 38px)", fontWeight: 800, margin: "12px 0 14px" }}>Ready to Understand Your Child More Deeply?</h2>
            <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 15, lineHeight: 1.75, marginBottom: 34 }}>Begin with a comprehensive assessment. Our team will build a clear picture of your child's strengths, priorities, and the most effective path forward.</p>
            <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 14, marginBottom: 30 }}>
              <a href="mailto:care@ingc.in" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: B.orange, color: "#fff", fontSize: 14, fontWeight: 700, padding: "14px 30px", borderRadius: 12, textDecoration: "none", transition: "all 0.2s", boxShadow: `0 4px 18px ${B.orange}40` }}><Mail size={18} /> care@ingc.in</a>
              <a href="https://wa.me/918122243094" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", fontSize: 14, fontWeight: 700, padding: "14px 30px", borderRadius: 12, textDecoration: "none" }}><Phone size={18} /> WhatsApp Us</a>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "rgba(255,255,255,0.55)", fontSize: 13 }}><MapPin size={14} /><span>Plot No 2, 10th Street, Balaji Nagar, Nanganallur, Chennai – 600061</span></div>
            <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 12, marginTop: 30, lineHeight: 1.6 }}>Therapy outcomes vary by individual. No specific results are guaranteed. All programmes are clinically individualised.</p>
          </Fade>
        </div>
      </section>
      <footer style={{ background: "#0c1a28", padding: "28px 20px", textAlign: "center" }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <div style={{ /*background: "rgba(255,255,255,0.08)", borderRadius: 12, */ padding: "10px 24px", display: "inline-block", margin: "0 auto 14px" }}>
            <img src="logowithtext.png" alt="Impact Neurogrowth Centre" style={{ height: 40, objectFit: "contain", display: "block", filter: "brightness(0) invert(1) brightness(1.1)" }} />
          </div>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, marginBottom: 6 }}>Plot No 2, 10th Street, Balaji Nagar, Nanganallur, Chennai – 600061</p>
          <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 11 }}>+91 81222 43094 (WhatsApp) · care@ingc.in</p>
          <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 14 }}>
            {[B.blue, B.red, B.orange, B.green].map((c, i) => (<div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: c, opacity: 0.6 }} />))}
          </div>
          <p style={{ color: "rgba(255,255,255,0.25)", fontSize: 10, marginTop: 12 }}>Specialised Neurodevelopmental Support · Evidence-Informed Therapy · Integrative Care</p>
        </div>
      </footer>
    </>
  );
};

/* ═══════════════════════════════════════════
   MAIN EXPORT
   ═══════════════════════════════════════════ */
const ImpactNeurogrowthInfoPage: React.FC = () => {
  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", color: B.gray800, WebkitFontSmoothing: "antialiased" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        @media (min-width: 768px) {
          .grid-2col { grid-template-columns: 1fr 1fr !important; }
          .nav-desktop { display: flex !important; }
          .nav-mobile-btn { display: none !important; }
          .nav-mobile-menu { display: none !important; }
          .nav-logo-icon { display: none !important; }
          .nav-logo-full { display: block !important; }
        }
        @media (max-width: 767px) {
          .nav-desktop { display: none !important; }
          .nav-mobile-btn { display: block !important; }
          .nav-mobile-menu { display: block !important; }
          .nav-logo-icon { display: block !important; }
          .nav-logo-full { display: none !important; }
        }
        @media (min-width: 1024px) {
          .grid-2col-lg { grid-template-columns: 1fr 1fr !important; }
          .team-grid { grid-template-columns: repeat(3, 1fr) !important; gap: 14px !important; }
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .team-grid { grid-template-columns: repeat(3, 1fr) !important; }
        }
      `}</style>
      <Navbar />
      <Hero />
      <Pillars />
      <Philosophy />
      <InaiModel />
      <Services />
      <TherapyJourney />
      <Team />
      <Testimonials />
      <ForParents />
      <SafetyEthics />
      <CtaFooter />
    </div>
  );
};

export default ImpactNeurogrowthInfoPage;
